import { Link } from 'react-router-dom';
import { Camera, CalendarCheck, Calculator } from 'lucide-react';
import { Card } from '@/components/ui/Card';

/**
 * GetStartedHero — the first-value card on the Command Center home for a
 * brand-new tenant (Usability audit 4/6, F-activation). Today a freshly
 * onboarded physician lands on an empty status page with nothing to DO; this
 * gives three concrete 5-minute wins and surfaces the auto-seeded compliance
 * calendar as the day-1 payoff. Gated by the same first-run window as
 * FirstRunChecklist (entity < 60d + required items remain) — see CommandCenter.
 *
 * Dark mode: light tint + paired dark: variant per the app's status-color
 * convention (design audit 5/6). Rows reflow cleanly at 375px.
 */
export function GetStartedHero({ deadlines = 0 }: { deadlines?: number }) {
  const actions = [
    {
      to: '/capture',
      icon: Camera,
      tint: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
      title: 'Capture your first expense',
      detail: 'Snap a receipt — Nexus tags the IRS code for you.',
      cta: 'Capture',
      primary: true,
    },
    {
      to: '/compliance',
      icon: CalendarCheck,
      tint: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      title: deadlines > 0
        ? `Your compliance calendar is ready · ${deadlines} tracked`
        : 'Your compliance calendar is ready',
      detail: 'Deadlines tracked automatically from your setup answers.',
      cta: 'View',
    },
    {
      to: '/tax',
      icon: Calculator,
      tint: 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300',
      title: 'See your tax projection',
      detail: 'Estimated quarterly taxes, the moment you add income.',
      cta: 'View',
    },
  ];

  return (
    <Card padding="lg">
      <p className="text-sm font-semibold text-slate-900 dark:text-white">Get your first value in about 5 minutes</p>
      <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5 mb-4">Three quick wins to bring your practice to life.</p>
      <div className="flex flex-col gap-2">
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <Link
              key={a.to}
              to={a.to}
              className="group flex items-center gap-3 rounded-[var(--radius-md)] border border-slate-200 dark:border-slate-700 p-3 hover:border-primary-400 dark:hover:border-primary-600 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
            >
              <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${a.tint}`}>
                <Icon className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-slate-900 dark:text-white truncate">{a.title}</span>
                <span className="block text-xs text-slate-600 dark:text-slate-400">{a.detail}</span>
              </span>
              <span
                className={`shrink-0 text-sm font-medium px-3.5 py-1.5 rounded-[var(--radius-md)] ${
                  a.primary
                    ? 'bg-primary-700 text-white group-hover:bg-primary-800'
                    : 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 group-hover:border-primary-400'
                }`}
              >
                {a.cta}
              </span>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}
